import React from 'react';
import {
  EnvironmentLogic,
  SlotControl,
  type EvaluatedForm,
  type AnsweredSlot,
  type SlotRecipients,
} from '@/modules/form';
import type { SelectSlot } from '@/modules/form/schema';
import styles from './ConnectionField.module.css';

/**
 * How a resharer can say they know whoever sent them the leg they pass on,
 * each written in the first person so the story can name that person with it
 * ("I reshared this from Ana, who I work with", "I reshared this from my wife Ana").
 * Copy rather than a tunable, so it lives
 * beside the one component that asks it. In alphabetical order.
 */
export const CONNECTION_OPTIONS: readonly string[] = [
  'I go to school with',
  'I share a house with',
  'I share an apartment with',
  'I work out with',
  'I work with',
  'my friend',
  'my husband',
  'my partner',
  'my wife',
];

/**
 * The question as a select, so it is answered with the same chips and
 * write-in as every other question in the composer.
 */
const CONNECTION_SLOT: SelectSlot = {
  type: 'select',
  id: 'connectionToPrevLeg',
  prompt: 'How you know the person who sent this to you',
  options: [...CONNECTION_OPTIONS],
  writeIn: { label: 'Other' },
};

// The options are plain words: they resolve against nothing and name no
// recipient, so the control is handed an empty environment.
const EMPTY_ENV = EnvironmentLogic.theEmptyEnvironment;
const UNBOUND_FORM: EvaluatedForm = {
  resultingEnvironment: EMPTY_ENV,
  slotEnvironments: new Map(),
  body: () => '',
};
const NO_RECIPIENTS: SlotRecipients = {
  contacts: [],
  markedFormFor: () => UNBOUND_FORM,
};

/**
 * The resharer's connection to whoever sent them what they are passing on.
 * It is a fact about the sender rather than about any one recipient, so the
 * garden composer asks it once for the whole reshare, above every message's
 * own questions, and each message's story reads it through
 * `{_connectionToPrevLeg}`.
 */
const ConnectionField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** The words last left in its write-in, kept by the caller. */
  lastWriteIn?: string;
  onLeaveWriteIn?: (words: string) => void;
}> = ({ value, onChange, disabled, lastWriteIn, onLeaveWriteIn }) => {
  const slot: AnsweredSlot<SelectSlot> = { ...CONNECTION_SLOT, answer: value };
  return (
    <section
      className={styles.connection}
      data-testid="garden-composer-connection-section"
    >
      <SlotControl
        slot={slot}
        env={EMPTY_ENV}
        addressingMode="dm"
        recipients={NO_RECIPIENTS}
        disabled={disabled}
        testId="garden-composer-connection"
        onAnswer={onChange}
        lastWriteIn={lastWriteIn}
        onLeaveWriteIn={onLeaveWriteIn}
      />
    </section>
  );
};

export default ConnectionField;
